<script lang="ts">
    import Popup from "../Popup.svelte"
    import PopupButtons from "../PopupButtons.svelte"
    import PopupExit from "../PopupExit.svelte"
    import PopupBanner from "../PopupBanner.svelte"
    import PopupIconDivider from "../PopupIconDivider.svelte"
    import PopupIcon from "../PopupIcon.svelte"
    import PopupHeading from "../PopupHeading.svelte"
    import PopupParagraph from "../PopupParagraph.svelte"
    import Button from "@components/button/Button.svelte"
    import {
        extractEmoji,
        newTab,
        selectAndGotoTab,
        setName,
        type TabClass,
    } from "@store/pageTracker"
    import { createEventDispatcher } from "svelte"
    import { ValidationError } from "@utils/underhood/errors-extended"

    // This component is also responsible for creating new tabs
    // If tab is null, it means we're creating a tab
    export let tab: TabClass | null

    let name = tab ? tab.name : "📖 Exploring"
    $: emoji = extractEmoji(name)

    const dispatch = createEventDispatcher()
    const finish = () => dispatch("exit")

    async function save() {
        try {
            if (tab) {
                setName(tab, name)
            } else {
                const createdTab = await newTab("", name)
                selectAndGotoTab(createdTab)
            }
            finish()
        } catch (e) {
            if (e instanceof ValidationError) {
                text.setCustomValidity(e.message)
                text.reportValidity()
            }
        }
    }

    let text: HTMLInputElement
</script>

<Popup>
    <PopupExit on:exit />
    <PopupBanner>
        <PopupIconDivider>
            <div slot="icon" class="tab-icon-preview">
                {#if emoji}
                    <span class="tab-emoji-preview">{emoji}</span>
                {:else}
                    <PopupIcon icon={tab ? "pen" : "new-tab"} />
                {/if}
            </div>
            <PopupHeading>
                <slot name="title" />
                {#if tab}
                    Rename “<span class:untitled={!tab.nameWithoutEmoji}>{tab.displayName}</span>”
                {:else}
                    Create new tab
                {/if}
            </PopupHeading>
        </PopupIconDivider>
    </PopupBanner>
    <form on:submit|preventDefault={save}>
        <PopupParagraph>
            <slot name="paragraph">
                {#if !tab}
                    <!-- We're creating a new tab -->
                    Tabs save your place and move with you as you progress through the Course.
                {/if}
            </slot>
        </PopupParagraph>
        <input bind:this={text} type="text" class="w-full default" bind:value={name} />
        <div class="tab-help">
            <i>(Optional)</i> Add your own emoji at the beginning to choose the icon for your tab.
        </div>
        <PopupButtons>
            <Button type="button" color="white" go={finish}>Cancel</Button>
            <Button type="submit" color="purple">{tab ? "Save" : "Create tab"}</Button>
        </PopupButtons>
    </form>
</Popup>

<style>
    form {
        display: grid;
        gap: 0.85rem;
    }

    .tab-icon-preview {
        display: grid;
        place-items: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: 999px;
        background: var(--color-purple-100);
        color: var(--color-purple-800);
        font-size: 1.6rem;
    }

    .tab-emoji-preview {
        display: grid;
        place-items: center;
        width: 2rem;
        height: 2rem;
        line-height: 1;
    }

    :global(.untitled) {
        font-style: italic;
    }

    form :global(input.default) {
        border: 1px solid var(--color-purple-100);
        border-radius: 0.75rem;
        background: var(--color-grey-0);
        color: var(--color-grey-900);
        font: inherit;
        padding: 0.85rem 1rem;
    }

    form :global(input.default:focus) {
        border-color: var(--color-purple-300);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-purple-100) 70%, transparent);
        outline: none;
    }

    .tab-help {
        border: 1px solid var(--color-purple-100);
        border-radius: 0.75rem;
        background: var(--color-purple-50);
        color: var(--color-grey-600);
        font-size: 0.95rem;
        line-height: 1.55;
        padding: 0.85rem 1rem;
    }

    :global(.darkmode) form :global(input.default) {
        border-color: var(--color-grey-700);
        background: var(--color-grey-900);
        color: var(--color-grey-50);
    }

    :global(.darkmode) .tab-icon-preview {
        background: var(--color-grey-700);
        color: var(--color-purple-100);
    }

    :global(.darkmode) form :global(input.default:focus) {
        border-color: var(--color-grey-600);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-grey-700) 70%, transparent);
    }

    :global(.darkmode) .tab-help {
        border-color: var(--color-grey-700);
        background: var(--color-grey-800);
        color: var(--color-grey-200);
    }
</style>
